import {
  IPathMarkup,
  IPoint,
  IRect,
  isClosedPath,
  ISize,
  isFilled,
  isTextMarkup,
  ITextMarkup,
  MAX_DIMENSION_PX,
  PhotoMarkup,
  TEXT_FONT_FAMILY,
  TEXT_LINE_HEIGHT,
  TEXT_MIN_BOX_WIDTH,
  TEXT_MIN_WIDTH,
  TEXT_PADDING,
} from './photoMarkupModel'

export interface IViewport {
  scale: number
  offsetX: number
  offsetY: number
}

export type MeasureText = (text: string, font: string) => number

export interface ITextLayout {
  font: string
  lines: string[]
  fontPx: number
  lineHeightPx: number
  paddingPx: number
  width: number
  height: number
}

const ELLIPSE_STEPS = 64
const MAX_ZOOM = 5

export function fitWithin(
  width: number,
  height: number,
  max: number = MAX_DIMENSION_PX,
): ISize {
  const scale = Math.min(1, max / Math.max(width, height))
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  }
}

export function markupUnit(image: ISize, view: IViewport): number {
  const displayed = Math.max(image.width, image.height) * view.scale
  return Math.max(image.width, image.height) / displayed
}

export function fitSize(box: ISize, image: ISize): ISize {
  const scale = Math.min(box.width / image.width, box.height / image.height)
  return { width: image.width * scale, height: image.height * scale }
}

export function fitViewport(stage: ISize, image: ISize): IViewport {
  const { width, height } = fitSize(stage, image)
  return {
    scale: width / image.width,
    offsetX: (stage.width - width) / 2,
    offsetY: (stage.height - height) / 2,
  }
}

function clampOffset(offset: number, stage: number, image: number): number {
  if (image <= stage) return (stage - image) / 2
  return Math.min(0, Math.max(stage - image, offset))
}

export function clampViewport(
  view: IViewport,
  stage: ISize,
  image: ISize,
): IViewport {
  const fit = fitViewport(stage, image).scale
  const scale = Math.min(Math.max(view.scale, fit), fit * MAX_ZOOM)
  return {
    scale,
    offsetX: clampOffset(view.offsetX, stage.width, image.width * scale),
    offsetY: clampOffset(view.offsetY, stage.height, image.height * scale),
  }
}

export function zoomViewport(
  view: IViewport,
  stage: ISize,
  image: ISize,
  from: IPoint,
  to: IPoint,
  factor: number,
): IViewport {
  const anchor = toImagePoint(from, view)
  const { scale } = clampViewport(
    { ...view, scale: view.scale * factor },
    stage,
    image,
  )
  return clampViewport(
    {
      scale,
      offsetX: to.x - anchor.x * scale,
      offsetY: to.y - anchor.y * scale,
    },
    stage,
    image,
  )
}

export function toImagePoint(screen: IPoint, view: IViewport): IPoint {
  return {
    x: (screen.x - view.offsetX) / view.scale,
    y: (screen.y - view.offsetY) / view.scale,
  }
}

export function toScreenPoint(image: IPoint, view: IViewport): IPoint {
  return {
    x: image.x * view.scale + view.offsetX,
    y: image.y * view.scale + view.offsetY,
  }
}

export function pointsBounds(points: IPoint[]): IRect {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const { x, y } of points) {
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  return { minX, minY, maxX, maxY }
}

export function rectCorners(rect: IRect): IPoint[] {
  return [
    { x: rect.minX, y: rect.minY },
    { x: rect.maxX, y: rect.minY },
    { x: rect.maxX, y: rect.maxY },
    { x: rect.minX, y: rect.maxY },
  ]
}

function ellipsePoints(a: IPoint, b: IPoint): IPoint[] {
  const cx = (a.x + b.x) / 2
  const cy = (a.y + b.y) / 2
  const rx = Math.abs(b.x - a.x) / 2
  const ry = Math.abs(b.y - a.y) / 2
  return Array.from({ length: ELLIPSE_STEPS }, (_, i) => {
    const angle = (i / ELLIPSE_STEPS) * Math.PI * 2
    return { x: cx + rx * Math.cos(angle), y: cy + ry * Math.sin(angle) }
  })
}

export function outlinePoints(markup: IPathMarkup): IPoint[] {
  if (markup.kind !== 'shape' || markup.shapeKind === 'polygon') {
    return markup.points
  }
  const [a, b] = markup.points
  return markup.shapeKind === 'circle'
    ? ellipsePoints(a, b)
    : rectCorners(pointsBounds([a, b]))
}

function distanceToSegment(p: IPoint, a: IPoint, b: IPoint): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lengthSq = dx * dx + dy * dy
  const t = lengthSq
    ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq))
    : 0
  return Math.hypot(p.x - (a.x + dx * t), p.y - (a.y + dy * t))
}

function distanceToPath(
  p: IPoint,
  points: IPoint[],
  isClosed: boolean,
): number {
  if (points.length === 1) {
    return Math.hypot(p.x - points[0].x, p.y - points[0].y)
  }
  let distance = Infinity
  const count = isClosed ? points.length : points.length - 1
  for (let i = 0; i < count; i++) {
    const next = points[(i + 1) % points.length]
    distance = Math.min(distance, distanceToSegment(p, points[i], next))
  }
  return distance
}

function isPointInPolygon(p: IPoint, points: IPoint[]): boolean {
  let isInside = false
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = points[i]
    const b = points[j]
    if (
      a.y > p.y !== b.y > p.y &&
      p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x
    ) {
      isInside = !isInside
    }
  }
  return isInside
}

function textFont(markup: ITextMarkup, unit: number): string {
  const style = markup.italic ? 'italic ' : ''
  const weight = markup.bold ? 700 : 400
  return `${style}${weight} ${markup.fontSize * unit}px ${TEXT_FONT_FAMILY}`
}

function breakWord(
  word: string,
  maxWidth: number,
  measure: (text: string) => number,
): string[] {
  const pieces: string[] = []
  let piece = ''
  for (const char of word) {
    if (piece && measure(piece + char) > maxWidth) {
      pieces.push(piece)
      piece = char
    } else {
      piece += char
    }
  }
  return [...pieces, piece]
}

function wrapText(
  paragraph: string,
  maxWidth: number,
  measure: (text: string) => number,
): string[] {
  const lines: string[] = []
  let line = ''
  for (const word of paragraph.split(' ')) {
    const candidate = line ? `${line} ${word}` : word
    if (measure(candidate) <= maxWidth) {
      line = candidate
      continue
    }
    if (line) lines.push(line)
    if (measure(word) <= maxWidth) {
      line = word
      continue
    }
    const pieces = breakWord(word, maxWidth, measure)
    lines.push(...pieces.slice(0, -1))
    line = pieces[pieces.length - 1]
  }
  return [...lines, line]
}

export function layoutText(
  markup: ITextMarkup,
  unit: number,
  measure: MeasureText,
): ITextLayout {
  const font = textFont(markup, unit)
  const measureLine = (text: string): number => measure(text, font)
  const fontPx = markup.fontSize * unit
  const lineHeightPx = fontPx * TEXT_LINE_HEIGHT
  const paddingPx = TEXT_PADDING * unit
  const paragraphs = markup.text.split('\n')
  const contentWidth =
    markup.boxWidth == null
      ? Math.max(TEXT_MIN_WIDTH * unit, ...paragraphs.map(measureLine))
      : markup.boxWidth * unit
  const lines =
    markup.boxWidth == null
      ? paragraphs
      : paragraphs.flatMap(p => wrapText(p, contentWidth, measureLine))
  return {
    font,
    lines,
    fontPx,
    lineHeightPx,
    paddingPx,
    width: contentWidth + paddingPx * 2,
    height: Math.max(lines.length * lineHeightPx, fontPx) + paddingPx * 2,
  }
}

export function markupBounds(
  markup: PhotoMarkup,
  unit: number,
  measure: MeasureText,
): IRect {
  if (isTextMarkup(markup)) {
    const { width, height } = layoutText(markup, unit, measure)
    return {
      minX: markup.at.x,
      minY: markup.at.y,
      maxX: markup.at.x + width,
      maxY: markup.at.y + height,
    }
  }
  return pointsBounds(markup.points)
}

export function expandRect(rect: IRect, by: number): IRect {
  return {
    minX: rect.minX - by,
    minY: rect.minY - by,
    maxX: rect.maxX + by,
    maxY: rect.maxY + by,
  }
}

export function isPointInRect(p: IPoint, rect: IRect): boolean {
  return (
    p.x >= rect.minX && p.x <= rect.maxX && p.y >= rect.minY && p.y <= rect.maxY
  )
}

export function hitsMarkup(
  markup: PhotoMarkup,
  p: IPoint,
  tolerance: number,
  unit: number,
  measure: MeasureText,
): boolean {
  if (isTextMarkup(markup)) {
    return isPointInRect(
      p,
      expandRect(markupBounds(markup, unit, measure), tolerance),
    )
  }
  const outline = outlinePoints(markup)
  const isClosed = isClosedPath(markup)
  if (isClosed && isFilled(markup) && isPointInPolygon(p, outline)) {
    return true
  }
  const reach = tolerance + (markup.strokeWidth * unit) / 2
  return distanceToPath(p, outline, isClosed) <= reach
}

export function topmostHit(
  markups: PhotoMarkup[],
  p: IPoint,
  tolerance: number,
  unit: number,
  measure: MeasureText,
): PhotoMarkup | null {
  for (let i = markups.length - 1; i >= 0; i--) {
    if (hitsMarkup(markups[i], p, tolerance, unit, measure)) return markups[i]
  }
  return null
}

export function translateMarkup<T extends PhotoMarkup>(
  markup: T,
  dx: number,
  dy: number,
): T {
  if (isTextMarkup(markup)) {
    return { ...markup, at: { x: markup.at.x + dx, y: markup.at.y + dy } }
  }
  return {
    ...markup,
    points: (markup as IPathMarkup).points.map(p => ({
      x: p.x + dx,
      y: p.y + dy,
    })),
  }
}

function clampExtent(anchor: number, target: number, minExtent: number) {
  const extent = target - anchor
  if (Math.abs(extent) >= minExtent) return target
  return anchor + (extent < 0 ? -minExtent : minExtent)
}

function resizeText(
  markup: ITextMarkup,
  corner: number,
  target: IPoint,
  unit: number,
  measure: MeasureText,
): ITextMarkup {
  const { width } = layoutText(markup, unit, measure)
  const padding = TEXT_PADDING * unit
  const isLeft = corner === 0 || corner === 3
  const right = markup.at.x + width
  const outerWidth = isLeft ? right - target.x : target.x - markup.at.x
  const boxWidth = Math.max(
    TEXT_MIN_BOX_WIDTH,
    (outerWidth - padding * 2) / unit,
  )
  const x = isLeft ? right - (boxWidth * unit + padding * 2) : markup.at.x
  return { ...markup, boxWidth, at: { x, y: markup.at.y } }
}

export function resizeMarkup<T extends PhotoMarkup>(
  markup: T,
  corner: number,
  target: IPoint,
  minExtent: number,
  unit: number,
  measure: MeasureText,
): T {
  if (isTextMarkup(markup)) {
    return resizeText(markup, corner, target, unit, measure) as T
  }
  const path = markup as IPathMarkup
  const corners = rectCorners(pointsBounds(path.points))
  const dragged = corners[corner]
  const anchor = corners[(corner + 2) % 4]
  const spanX = dragged.x - anchor.x
  const spanY = dragged.y - anchor.y
  const targetX = spanX ? clampExtent(anchor.x, target.x, minExtent) : 0
  const targetY = spanY ? clampExtent(anchor.y, target.y, minExtent) : 0
  const scaleX = spanX ? (targetX - anchor.x) / spanX : 1
  const scaleY = spanY ? (targetY - anchor.y) / spanY : 1
  return {
    ...markup,
    points: path.points.map(p => ({
      x: anchor.x + (p.x - anchor.x) * scaleX,
      y: anchor.y + (p.y - anchor.y) * scaleY,
    })),
  }
}

export function moveVertex(
  markup: IPathMarkup,
  index: number,
  target: IPoint,
): IPathMarkup {
  return {
    ...markup,
    points: markup.points.map((p, i) => (i === index ? { ...target } : p)),
  }
}

export function squareAround(center: IPoint, half: number): IPoint[] {
  return [
    { x: center.x - half, y: center.y - half },
    { x: center.x + half, y: center.y + half },
  ]
}

export function textOrigin(
  point: IPoint,
  fontSize: number,
  unit: number,
): IPoint {
  const padding = TEXT_PADDING * unit
  const lineHeight = fontSize * unit * TEXT_LINE_HEIGHT
  return { x: point.x - padding, y: point.y - padding - lineHeight / 2 }
}

export function snapToAngle(from: IPoint, to: IPoint, stepDeg = 45): IPoint {
  const distance = Math.hypot(to.x - from.x, to.y - from.y)
  const step = (stepDeg * Math.PI) / 180
  const angle =
    Math.round(Math.atan2(to.y - from.y, to.x - from.x) / step) * step
  return {
    x: from.x + distance * Math.cos(angle),
    y: from.y + distance * Math.sin(angle),
  }
}
